import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import SubmitButton from './SubmitButton';
import Grid from '@mui/material/Grid';
import useTransition from '../hooks/useTransition';
import type { IPolarisInterviewQuestion } from 'src/packages/polaris/types';
import useIsPolaris from '../hooks/useIsPolaris';
import TextFormInput from './FormElements/TextFormInput';
import InsuranceCarrierSearch from './FormElements/InsuranceCarriersSearch';
import FormLabel from './FormElements/FormLabel';
import SearchDropDown from './FormElements/SearchDropDown';

const INS_RELATIONSHIP_OPTIONS = [
  { name: 'Self', token: 'self' },
  { name: 'Spouse', token: 'spouse' },
  { name: 'Child', token: 'child' },
  { name: 'Other', token: 'other' },
];

export type InsuranceRelationship = 'self' | 'spouse' | 'child' | 'other';

export interface IInsuranceForm {
  planId: string;
  memberId?: string;
  group?: string;
  relationship: InsuranceRelationship;
}

const InsuranceForm = ({
  question,
  onQuestionSubmit,
}: {
  question: IPolarisInterviewQuestion;
  onQuestionSubmit?: (index: number) => void;
}) => {
  const transition = useTransition();
  const isPolaris = useIsPolaris();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<IInsuranceForm>({
    defaultValues: {
      planId: question?.answer?.planId,
      memberId: question?.answer?.memberId,
      group: question?.answer?.group,
      relationship: question?.answer?.relationship,
    },
  });

  /** This submit handler only fires when the data is valid */
  const onSubmit: SubmitHandler<IInsuranceForm> = (data) => {
    onQuestionSubmit?.(question.index);

    transition({
      data: {
        value: data,
        question: {
          ...question,
          key: 'insurance',
        },
        isPolaris,
      },
    });
  };

  return (
    <>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <FormLabel
            label="Your insurance information"
            name="planId"
            tooltipText="You can find your member ID on your insurance card. It should look something like ‘ABC000000000’."
          />
          <Controller
            control={control}
            name="planId"
            render={({ field }) => {
              const { ref: inputRef, ...fieldProps } = field;
              return <InsuranceCarrierSearch {...fieldProps} />;
            }}
          />
        </Grid>
        <Grid item>
          <TextFormInput control={control} name="memberId" placeholder="Member ID number" />
        </Grid>
        <Grid item>
          <TextFormInput control={control} name="group" placeholder="Insurance group number" />
        </Grid>
        <Grid item>
          <Controller
            control={control}
            name="relationship"
            render={({ field }) => {
              const { ref: inputRef, ...fieldProps } = field;
              return (
                <SearchDropDown
                  {...fieldProps}
                  options={INS_RELATIONSHIP_OPTIONS}
                  placeholder="Relationship to the insured"
                  onChange={(_event, value) => {
                    fieldProps.onChange(value.token);
                  }}
                />
              );
            }}
          />
        </Grid>
      </Grid>
      <SubmitButton onClick={handleSubmit(onSubmit)} text={isDirty ? 'Submit' : 'Skip'} />
    </>
  );
};

export default InsuranceForm;
